import { Button } from "@/components/ui/button.tsx";
import { Checkbox } from "@/components/ui/checkbox.tsx";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog.tsx";
import { Label } from "@/components/ui/label.tsx";
import { cn } from "@/lib/utils.ts";
import { format } from "date-fns";
import { ArrowUp } from "lucide-react";
import { useState } from "react";

/**
 * Confirm dialog shown after a drag-to-reschedule drop on an event that has
 * guests. Mirrors Notion's pattern: shows the new time prominently with the
 * old time struck through, and offers three action buttons — Send update,
 * Continue editing, Discard — plus the agent-prompt shortcut described below.
 *
 * When `allowSendWithoutEmail` is true (Google calendars), the dialog also
 * shows a "Don't send an email to guests" checkbox; checking it makes both
 * "Send update" and the agent shortcut suppress the guest notification
 * (`sendUpdates: "none"`). It is false for providers that can't suppress
 * notifications — Microsoft Graph always notifies on an organizer update — in
 * which case the checkbox is hidden and updates always notify.
 *
 * Per the contextual-agent-triggers spec (§3), this dialog also hosts an
 * agent shortcut: a suggestion-chip row + prompt input that fire
 * "send update + ask the agent for help" in one shot. Submitting either
 * route resolves with `sendUpdateAndAsk` so the caller can perform the
 * patch AND hand off to a new agent thread with the rescheduled context.
 */
export type RescheduleChoice =
  | { kind: "sendUpdate"; notify: boolean }
  | { kind: "sendUpdateAndAsk"; prompt: string; notify: boolean }
  | { kind: "continueEditing" }
  | { kind: "discard" };

const SUGGESTION_CHIPS: ReadonlyArray<string> = [
  "Draft a quick note to guests",
  "Propose alternate times",
  "Tell guests why I moved this",
];

export type EventRescheduleConfirmDialogProps = {
  open: boolean;
  oldStart: Date;
  oldEnd: Date;
  newStart: Date;
  newEnd: Date;
  isAllDay: boolean;
  allowSendWithoutEmail: boolean;
  /** Called with the user's choice. Caller is responsible for closing the dialog. */
  onChoose: (choice: RescheduleChoice) => void;
  /** Fired when the user dismisses the dialog (Escape, backdrop). Treated as "discard". */
  onClose: () => void;
};

function formatRange(start: Date, end: Date, isAllDay: boolean): string {
  if (isAllDay) {
    // For all-day events FullCalendar's end is exclusive (day-after).
    // Show just the start date — sufficient for the visual diff.
    return format(start, "EEE MMM d");
  }
  const sameDay = format(start, "yyyy-MM-dd") === format(end, "yyyy-MM-dd");
  if (sameDay) {
    return `${format(start, "EEE MMM d, h:mm a")} – ${format(end, "h:mm a")}`;
  }
  return `${format(start, "EEE MMM d, h:mm a")} – ${format(end, "EEE MMM d, h:mm a")}`;
}

export const EventRescheduleConfirmDialog = ({
  open,
  oldStart,
  oldEnd,
  newStart,
  newEnd,
  isAllDay,
  allowSendWithoutEmail,
  onChoose,
  onClose,
}: EventRescheduleConfirmDialogProps) => {
  const [prompt, setPrompt] = useState("");
  // Checked = "Don't send an email to guests". Only shown when the provider can
  // suppress notifications (Google); drives both the Send button and the agent
  // shortcut. Defaults to unchecked so the default behavior stays "notify".
  const [dontNotify, setDontNotify] = useState(false);
  const notify = !dontNotify;

  const submitAgent = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onChoose({ kind: "sendUpdateAndAsk", prompt: trimmed, notify });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* max-w-lg (not the default md) so three action buttons + a long time
          range comfortably fit on one row with the dialog's `p-6` gutters
          intact. With md (448px) the button row overflows or wraps. */}
      <DialogContent className="max-w-lg">
        <div className="space-y-5">
          <div>
            {/* Radix DialogTitle (not a raw h3) so the modal gets a proper
                ARIA accessible name via aria-labelledby on the dialog root. */}
            <DialogTitle className="text-lg font-semibold">
              Do you want to send the update?
            </DialogTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              You can send the update, keep the change and continue editing, or discard the change.
            </p>
          </div>

          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
            <div className="flex items-baseline gap-2">
              <span className="text-muted-foreground text-xs uppercase tracking-wide">Time</span>
              <span className="font-medium">{formatRange(newStart, newEnd, isAllDay)}</span>
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              {/* Invisible "Time" spacer to vertically align the struck-through
                  old-time row with the new-time row above. aria-hidden so
                  screen readers don't announce a duplicate "Time" label. */}
              <span
                aria-hidden="true"
                className="text-muted-foreground text-xs uppercase tracking-wide invisible"
              >
                Time
              </span>
              <span className="text-muted-foreground line-through">
                {formatRange(oldStart, oldEnd, isAllDay)}
              </span>
            </div>
          </div>

          {/* Agent shortcut — suggestion chips + prompt input that fires
              "send update + ask the agent for help" in one go. Chips
              prefill the input; the user submits via Enter or the send
              button. Sits between the time card and the action buttons
              per spec §3. */}
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-1.5">
              {SUGGESTION_CHIPS.map((label) => (
                <Button
                  key={label}
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={() => setPrompt(label)}
                  data-statsig-name="reschedule-agent-chip"
                  className="h-7 rounded-full px-2.5 text-xs bg-secondary hover:bg-card"
                >
                  {label}
                </Button>
              ))}
            </div>
            <div className="relative flex items-end gap-2 rounded-2xl border bg-popover px-3 py-2">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submitAgent(prompt);
                  }
                }}
                rows={1}
                placeholder="✨ Need help? Draft a note, propose alternates…"
                aria-label="Ask the agent"
                className={cn(
                  "relative block w-full resize-none border-none bg-transparent p-0 align-bottom",
                  "font-normal text-base min-h-7 leading-7 max-h-32 overflow-y-auto",
                  "placeholder:text-muted-foreground/60 text-popover-foreground",
                  "focus:outline-hidden",
                )}
              />
              <Button
                type="button"
                size="icon"
                variant="primary"
                disabled={prompt.trim().length === 0}
                onClick={() => submitAgent(prompt)}
                aria-label="Send update and ask the agent"
                data-statsig-name="reschedule-agent-submit"
                className="h-7 w-7 shrink-0"
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Actions row. The "Don't send an email to guests" checkbox (Google
              only) sits at the left; the Discard / Continue editing / Send
              update buttons stay right-aligned Notion-style. Discard is a
              ghost-red text button — the quietest option — while Send update is
              the filled primary CTA. On narrow viewports the checkbox stacks
              above the buttons (and the buttons wrap) so nothing overflows. */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {allowSendWithoutEmail ? (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="reschedule-dont-send-email"
                  checked={dontNotify}
                  onCheckedChange={(checked) => setDontNotify(checked === true)}
                  data-statsig-name="reschedule-dont-send-email"
                />
                <Label
                  htmlFor="reschedule-dont-send-email"
                  className="cursor-pointer font-normal text-muted-foreground"
                >
                  Don't send an email to guests
                </Label>
              </div>
            ) : (
              // Spacer keeps the buttons right-aligned when the checkbox is hidden.
              <span className="hidden sm:block" />
            )}
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                variant="ghost"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                data-statsig-name="reschedule-discard"
                onClick={() => onChoose({ kind: "discard" })}
                type="button"
              >
                Discard change
              </Button>
              <Button
                variant="outline"
                data-statsig-name="reschedule-continue-editing"
                onClick={() => onChoose({ kind: "continueEditing" })}
                type="button"
              >
                Continue editing
              </Button>
              <Button
                variant="primary"
                data-statsig-name="reschedule-send-update"
                onClick={() => onChoose({ kind: "sendUpdate", notify })}
                type="button"
              >
                Send update
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
